import React, { useEffect, useRef } from 'react';
import './LoadingOverlay.css';

interface LoadingOverlayProps {
  message: string;
}

/**
 * Blocking progress overlay. Focus moves here while it is open so a control
 * underneath cannot stay focused, and returns only if that control is still
 * visible and actionable when the overlay closes.
 */
const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    dialogRef.current?.focus();

    return () => {
      if (!previouslyFocused?.isConnected) return;
      if (previouslyFocused.hasAttribute('disabled')) return;
      if (previouslyFocused.getAttribute('aria-hidden') === 'true' || previouslyFocused.hidden) {
        return;
      }
      previouslyFocused.focus();
    };
  }, []);

  const keepFocusInOverlay = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div
      ref={dialogRef}
      className="loading-overlay"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-labelledby="loading-overlay-message"
      tabIndex={-1}
      onKeyDown={keepFocusInOverlay}
    >
      <div className="loading-overlay__card">
        <div className="loading-overlay__spinner" aria-hidden="true" />
        <p id="loading-overlay-message" className="loading-overlay__message" aria-live="polite">
          {message}
        </p>
      </div>
    </div>
  );
};

export default LoadingOverlay;
